import React from 'react';
import { Document, View } from '@react-pdf/renderer';
import { space } from './theme';
import { registerFonts } from './registerFonts';
import PageFrame from './pages/PageFrame';

import MeetingHeaderSection from './pages/SkimPage';
import MeetingDetailsSection from './pages/MeetingDetailsSection';
import SummarySection from './pages/SummarySection';
import WhoWeAreBannerSection from './pages/WhoWeAreBannerSection';
import ProblemAlignmentSection from './pages/ProblemAlignmentPage';
import { KeyMomentsSection, OpportunitiesSection } from './pages/DealIntelligencePage';
import { RisksSection, NextStepsSection } from './pages/RisksAndNextStepsPage';
import ProspectProfileSection from './pages/ProspectProfilePage';
import MurphyReviewSection from './pages/MurphysReviewPage';
import QABreakdownSection from './pages/QABreakdownPage';

registerFonts();

// Spacer between sections — uniform vertical rhythm. Smaller than the
// section-internal gaps so the document doesn't waste space between blocks.
const SectionGap = () => <View style={{ height: space.md }} />;

// All sections rendered as one continuous stream. @react-pdf paginates
// automatically when content overflows the page. Order mirrors the in-app
// expanded meeting card.
const MurphyReport = ({ report }) => {
  const isInternal = report.variant === 'internal';

  return (
    <Document
      title={`Murphy Report — ${report.meeting?.title || ''}`}
      author="Multiply"
      subject={isInternal ? 'Internal review' : 'Shared with prospect'}
    >
      <PageFrame variant={report.variant}>
        <MeetingHeaderSection report={report} />
        <SectionGap />
        <MeetingDetailsSection report={report} />
        <SectionGap />
        <SummarySection report={report} />

        {isInternal && (
          <>
            <SectionGap />
            <WhoWeAreBannerSection report={report} />
          </>
        )}

        {/* Problem Alignment is an internal sales-fit assessment of the
            prospect — reads as "we evaluated how well you fit our funnel".
            Internal-only. */}
        {isInternal && (
          <>
            <SectionGap />
            <ProblemAlignmentSection report={report} />
          </>
        )}

        {/* Murphy review (Positives / Areas) sits directly under Problem
            Alignment so the headline coaching artifact reads next to the
            deal-fit context, mirroring the in-app expanded meeting card.
            Internal-only — never on the shared variant. */}
        {isInternal && (
          <>
            <SectionGap />
            <MurphyReviewSection report={report} />
          </>
        )}

        {/* Key Moments renders for both variants — the shared variant strips
            the rep-perspective type taxonomy and the "Why it matters" coaching
            field at the data layer in mapToReport. Opportunities and Risks
            stay internal-only — the upsell/risk registers read as sales
            artifacts. */}
        <SectionGap />
        <KeyMomentsSection report={report} />

        {isInternal && (
          <>
            <SectionGap />
            <OpportunitiesSection report={report} />
            <SectionGap />
            <RisksSection report={report} />
          </>
        )}

        <SectionGap />
        <NextStepsSection report={report} />

        {/* Prospect profile and Q&A breakdown are internal coaching surfaces.
            The Q&A coaching tip is already stripped per-question in mapToReport
            for shared; gating the whole section keeps the prospect from seeing
            the question grades too. Sales-rep-performance section was removed
            — it duplicated coaching content already in Areas For Improvement
            and Murphy's Review without adding new information. */}
        {isInternal && (
          <>
            <SectionGap />
            <ProspectProfileSection report={report} />
            <SectionGap />
            <QABreakdownSection report={report} />
          </>
        )}
      </PageFrame>
    </Document>
  );
};

export default MurphyReport;
